import { DIGITAL_LOAN_STEPS } from 'constants/digitalLoan';
import {
  DIGITAL_LOAN_PHASE3_BACKEND,
  LOCAL_VISIT_STATUS,
} from 'constants/digitalLoanPhase3';
import { inferVisitStatus } from './phase3Display';
import {
  isPhase3Closed,
  resolvePhase4Entry,
} from './phase4Routing';

/**
 * True si status indica solicitud activa en Phase 3.
 * TODO: mapear enums adicionales cuando Backend documente el catálogo.
 */
export function isPhase3InProgress(status) {
  if (status?.hasActiveApplication === false) return false;
  const app = status?.activeApplication;
  if (!app) return false;
  if (status?.hasApplicationAwaitingDisbursement === true) {
    return false;
  }
  if (resolvePhase4Entry({ status })) return false;
  if (isPhase3Closed(status)) return false;
  const phase = app.currentPhase;
  const appStatus = app.status;
  return (
    phase === DIGITAL_LOAN_PHASE3_BACKEND.PHASE
    && (
      !appStatus
      || appStatus === DIGITAL_LOAN_PHASE3_BACKEND.STATUS_IN_PROGRESS
    )
  );
}

/**
 * Hay una solicitud que se puede retomar.
 * No depende de la fase: Fase 1–4 y desembolso pendiente cuentan.
 * Una solicitud cerrada no viene como hasActiveApplication.
 */
export function hasResumableDigitalLoanApplication(status) {
  if (status?.hasApplicationAwaitingDisbursement === true) return true;
  return status?.hasActiveApplication === true && !!status?.activeApplication;
}

/**
 * Card de Home: Fase 3 en curso, contratación 4a o contrato firmado
 * esperando dispersión. La dispersión no tiene pantalla propia.
 */
export function shouldShowDigitalLoanHomeCard(status) {
  if (status?.hasApplicationAwaitingDisbursement === true) return true;
  if (status?.hasActiveApplication !== true) return false;
  if (resolvePhase4Entry({ status })) return true;
  if (isPhase3Closed(status)) return true;
  return isPhase3InProgress(status);
}

function resolveRejectionStep(rejectionReason) {
  if (rejectionReason === 'VALUE_INSUFFICIENT') {
    return DIGITAL_LOAN_STEPS.INSUFFICIENT_VALUE;
  }
  if (rejectionReason === 'KNOCK_OUT') {
    return DIGITAL_LOAN_STEPS.NOT_PREAPPROVED;
  }
  // TODO: map other rejectionReason values when Backend documents the catalog.
  return null;
}

/**
 * Resuelve el paso FE al abrir la card de Home.
 * Prioridad: currentPhase/currentStep, luego resúmenes de fase,
 * y el snapshot solo para presentación de la visita.
 */
export function resolveDigitalLoanEntryStep({
  currentStep,
  currentPhase,
  status,
  snapshot,
  isRobustOnboardingComplete,
} = {}) {
  const app = status?.activeApplication || {};
  const gestoria = status?.gestoria || {};
  const step = currentStep || app.currentStep;
  const phase = currentPhase || app.currentPhase;

  const phase4Step = resolvePhase4Entry({
    currentStep: step,
    currentPhase: phase,
    status,
  });
  if (phase4Step) return phase4Step;

  const rejectionStep = resolveRejectionStep(app.rejectionReason);
  if (rejectionStep) return rejectionStep;

  if (isPhase3Closed(status)) {
    return DIGITAL_LOAN_STEPS.FINAL_OFFER;
  }

  const visitStatus = inferVisitStatus(status, snapshot);
  const hasScheduledVisit = (
    !!gestoria.scheduledVisitAt
    || visitStatus === LOCAL_VISIT_STATUS.SCHEDULED
    || visitStatus === LOCAL_VISIT_STATUS.RESCHEDULED
    || !!snapshot?.scheduledVisitAt
  );

  if (
    !isRobustOnboardingComplete
    && !hasScheduledVisit
    && visitStatus !== LOCAL_VISIT_STATUS.CANCELLED
  ) {
    return DIGITAL_LOAN_STEPS.MISSING_ONBOARDING;
  }

  if (
    hasScheduledVisit
    || visitStatus === LOCAL_VISIT_STATUS.CANCELLED
  ) {
    return DIGITAL_LOAN_STEPS.VISIT_STATUS;
  }

  if (
    phase === DIGITAL_LOAN_PHASE3_BACKEND.PHASE
    && step === DIGITAL_LOAN_PHASE3_BACKEND.STEP_SCHEDULING
  ) {
    return DIGITAL_LOAN_STEPS.PRE_OFFER;
  }

  if (step === DIGITAL_LOAN_PHASE3_BACKEND.STEP_SCHEDULING) {
    return DIGITAL_LOAN_STEPS.PRE_OFFER;
  }

  // TODO: map future backend currentStep enums for Phase 3 visit states.
  return DIGITAL_LOAN_STEPS.PRE_OFFER;
}
